/**
 * El globo de la mascota: el saludo, lo que se le ocurre hoy, y el buscador.
 *
 * Antes el saludo era el título de la pantalla, en un encabezado como los de
 * cualquier otra sección. Puesto en un globo lo dice ALGUIEN, y esta pantalla
 * es la única del panel que recibe en vez de listar.
 *
 * Aquí estuvo el buscador del panel un rato, hasta que se mudó a la barra de
 * arriba: en la primera pantalla sólo estaba a mano estando en la primera
 * pantalla. Ver `DashboardSearch`.
 */
import { useEffect, useState, type ReactNode } from 'react'
import { IconArrowRight } from '@tabler/icons-react'
import { pickMascotLine, type MascotFact } from '../../lib/mascotLines'

export function MascotGreeting({ greeting, facts, ready = true, pinned, children }: {
  /** «Buenos días, Heriberto». Lo arma quien sabe la hora y el nombre. */
  greeting: string
  /**
   * Las frases que sí saben algo, ya escritas por quien tiene los datos.
   *
   * Llegan hechas y no en crudo a propósito: aquí no hay forma de saber si un
   * 24% es bueno ni si tres formularios son muchos, y una frase armada a ciegas
   * con cifras ajenas es como se cuelan los datos que no significan nada.
   */
  facts?: readonly MascotFact[]
  /**
   * Si los datos del proyecto ya llegaron.
   *
   * Sin esto la frase se elegía en el primer pintado, cuando todavía no se sabe
   * nada: el proyecto aún no tiene subdominio, así que «tu sitio todavía no está
   * publicado» era cierta para el componente y falsa para el cliente — y se
   * quedaba congelada, porque la frase se elige una sola vez. Decirle a alguien
   * que su sitio no está publicado cuando lo está es peor que no decir nada.
   */
  ready?: boolean
  /**
   * Una frase concreta, cuando el momento ya tiene algo que decir.
   *
   * Con esto puesto no se sortea nada: hay ocasiones en que el globo no está
   * rellenando el día sino contando lo que acaba de pasar —acabas de cambiar de
   * proyecto—, y un consejo al azar debajo de esa noticia la convierte en otra
   * frase más del montón. Quien pone la frase es quien sabe por qué llegaste.
   */
  pinned?: MascotFact | undefined
  /** Lo que va debajo de la frase, en el mismo globo: el asistente de Inicio. */
  children?: ReactNode
}) {
  /* Una vez por visita, no en cada repintado: la frase que cambia sola mientras
     lees es un error, no una gracia. Pero no antes de tiempo: hasta que los
     datos están, no hay nada que decir. */
  const [line, setLine] = useState<MascotFact | null>(null)

  useEffect(() => {
    if (pinned || !ready || line) return
    setLine(pickMascotLine(facts))
  }, [pinned, ready, line, facts])

  const shown = pinned ?? line

  return (
    <div className="dash-mascot-bubble">
      <p className="dash-mascot-bubble-greeting">{greeting}</p>
      {/* Al llegar una frase, esta caja se abre desde el saludo. La clave la
          remonta cuando la mascota cambia de idea y vuelve a disparar el gesto. */}
      {shown ? (
        <div className="dash-mascot-bubble-message" key={`${shown.text}:${shown.action?.label ?? ''}`}>
          <p className="dash-mascot-bubble-line">
            {shown.text}
            {/* El botón EN el renglón y no debajo: es la respuesta a lo que se
                acaba de decir, y en una fila aparte se lee como una acción de la
                pantalla en vez de como la salida a lo que la mascota señaló. */}
            {shown.action ? (
              <button className="dash-mascot-bubble-action" type="button" onClick={shown.action.run}>
                {shown.action.label} <IconArrowRight size={12} aria-hidden="true" />
              </button>
            ) : null}
          </p>
        </div>
      ) : null}
      {children}
    </div>
  )
}
